import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import axios from 'axios';
import { API_URL } from '../../config/api';

// Async thunks
export const fetchProducts = createAsyncThunk(
  'products/fetchProducts',
  async ({ page = 1, limit = 12, category, search, subcategory } = {}, { rejectWithValue }) => {
    try {
      const params = new URLSearchParams();
      if (page) params.append('page', page);
      if (limit) params.append('limit', limit);
      if (category) params.append('category', category);
      if (search) params.append('search', search);
      if (subcategory) params.append('subcategory', subcategory);

      const response = await axios.get(`${API_URL}/api/public/products?${params.toString()}`);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch products');
    }
  }
);

export const fetchProductById = createAsyncThunk(
  'products/fetchProductById',
  async (id, { rejectWithValue }) => {
    try {
      const response = await axios.get(`${API_URL}/api/public/products/${id}`);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch product');
    }
  }
);

export const fetchFeaturedProducts = createAsyncThunk(
  'products/fetchFeaturedProducts',
  async (_, { rejectWithValue }) => {
    try {
      const response = await axios.get(`${API_URL}/api/public/products?limit=4`);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch featured products');
    }
  }
);

const productsSlice = createSlice({
  name: 'products',
  initialState: {
    items: [],
    featuredItems: [],
    currentProduct: null,
    pagination: {
      currentPage: 1,
      totalPages: 1,
      totalItems: 0,
      itemsPerPage: 12,
    },
    filters: {
      category: null,
      subcategory: null,
      search: '',
    },
    loading: false,
    error: null,
  },
  reducers: {
    setFilters: (state, action) => {
      state.filters = { ...state.filters, ...action.payload };
      state.pagination.currentPage = 1; // Reset to first page on filter change
    },
    setPage: (state, action) => {
      state.pagination.currentPage = action.payload;
    },
    clearFilters: (state) => {
      state.filters = {
        category: null,
        subcategory: null,
        search: '',
      };
      state.pagination.currentPage = 1;
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch products
      .addCase(fetchProducts.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchProducts.fulfilled, (state, action) => {
        state.loading = false;
        state.items = action.payload.data || [];
        state.pagination.totalItems = action.payload.total || action.payload.count || 0;
        state.pagination.currentPage = action.payload.page || state.pagination.currentPage;
        state.pagination.totalPages = action.payload.totalPages || Math.ceil(
          (action.payload.total || action.payload.count || 0) / state.pagination.itemsPerPage
        );
        
        // 🔍 DEBUG: Log image URLs from API response
        if (action.payload.data && action.payload.data.length > 0) {
          console.log('📦 [Products] Received products:', action.payload.data.length);
          action.payload.data.slice(0, 3).forEach((product, idx) => {
            try {
              if (product && product.name) {
                console.log(`   Product ${idx + 1}: ${product.name}`);
                if (product.images && Array.isArray(product.images) && product.images.length > 0) {
                  const imageUrl = product.images[0];
                  if (imageUrl && typeof imageUrl === 'string') {
                    console.log(`   └─ Image URL: ${imageUrl.substring(0, 80)}...`);
                    console.log(`   └─ Is S3: ${imageUrl.includes('amazonaws.com')}`);
                    console.log(`   └─ Is Pre-signed: ${imageUrl.includes('X-Amz')}`);
                  } else {
                    console.log(`   └─ Image URL is null or invalid`);
                  }
                } else {
                  console.log(`   └─ No images array`);
                }
              }
            } catch (err) {
              console.error(`   ❌ Error logging product ${idx + 1}:`, err.message);
            }
          });
        }
      })
      .addCase(fetchProducts.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // Fetch product by ID
      .addCase(fetchProductById.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchProductById.fulfilled, (state, action) => {
        state.loading = false;
        state.currentProduct = action.payload.data;
      })
      .addCase(fetchProductById.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // Fetch featured products
      .addCase(fetchFeaturedProducts.pending, (state) => {
        state.loading = true;
      })
      .addCase(fetchFeaturedProducts.fulfilled, (state, action) => {
        state.loading = false;
        state.featuredItems = action.payload.data?.slice(0, 4) || [];
      })
      .addCase(fetchFeaturedProducts.rejected, (state) => {
        state.loading = false;
      });
  },
});

export const { setFilters, setPage, clearFilters } = productsSlice.actions;
export default productsSlice.reducer;

